import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuthStore } from '../auth/store';
import { api, getApiBaseUrl } from './client';

export type LiveStreamStatus = 'pending' | 'pushing' | 'on_air' | 'ended' | 'rejected';

export interface LiveStreamCorrespondent {
  id: string;
  name: string | null;
  email: string;
  avatarUrl: string | null;
}

export interface LiveStreamItem {
  id: string;
  status: LiveStreamStatus;
  onAirSlot: number | null;
  correspondent: LiveStreamCorrespondent | null;
  title: string | null;
  locationLabel: string | null;
  geoLat: number | null;
  geoLng: number | null;
  bitrateKbps: number | null;
  packetLossPct: number | null;
  createdAt: string;
  pushStartedAt: string | null;
  promotedAt: string | null;
  streamKey: string;
  /** Set when the session was started under a persistent named stream
   *  key. Null for mobile-correspondent + ad-hoc OBS sessions. */
  sourceStreamKeyId: string | null;
  pullUrlSrt: string | null;
  pullUrlRtmp: string | null;
  /** Public HLS URL the green-room preview player loads. Null when
   *  the operator hasn't set up an HTTPS reverse-proxy in front of
   *  SRS:8080 (LIVE_HLS_PUBLIC_BASE unset). */
  hlsPreviewUrl: string | null;
  /** WHEP endpoint for the sub-second WebRTC preview (SRS's `live`
   *  app). Carries video plus Opus audio, which SRS transcodes from the
   *  publisher's AAC in its `rtmp_to_rtc` bridge. Null when
   *  LIVE_WEBRTC_PUBLIC_BASE isn't set; admin falls back to HLS. */
  webrtcPlayUrl: string | null;
  /** WHIP endpoint the operator's push-to-talk control publishes their
   *  microphone to, so the correspondent hears them in their earpiece.
   *  Audio-only, and a separate SRS app from `live` — talkback never
   *  reaches the outbound broadcast, the DVR, or a studio pull. Null
   *  when LIVE_WEBRTC_PUBLIC_BASE isn't set; the control is hidden. */
  talkbackPublishUrl: string | null;
}

export interface LiveSlot {
  slot: number;
  streamId: string | null;
  /** Stable pull URL for this slot — same across promotes, so vMix
   *  can pre-configure three SRT inputs once. */
  pullUrlSrt: string | null;
  pullUrlRtmp: string | null;
}

export interface LiveListResponse {
  streams: LiveStreamItem[];
  slots: LiveSlot[];
  ingestHost: string | null;
}

const queryKey = ['live', 'active'] as const;

/**
 * SSE-driven live state. Connects to `/admin/live/stream` and writes
 * each snapshot into the React Query cache. A long fallback poll
 * (60 s) catches the case where the connection silently dies behind a
 * proxy without firing `onerror`.
 *
 * Token is passed as `?access_token=` because browser EventSource
 * can't set headers. The admin middleware accepts the query param
 * only when the Authorization header is absent.
 */
export function useLiveStreams() {
  const qc = useQueryClient();
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    if (!token) return;
    const base = getApiBaseUrl();
    const url = `${base}/admin/live/stream?access_token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    es.addEventListener('snapshot', (e) => {
      try {
        const snapshot = JSON.parse((e as MessageEvent).data) as LiveListResponse;
        qc.setQueryData(queryKey, snapshot);
      } catch {
        // Ignore — malformed events are extremely unlikely and we'll
        // catch up on the next snapshot.
      }
    });

    // No explicit handler for 'ping' — we just need the bytes to keep
    // the connection alive past proxy idle timeouts. EventSource
    // re-connects on its own when the server closes; if the server
    // is gone the long-fallback refetchInterval below catches it.
    return () => {
      es.close();
    };
  }, [qc, token]);

  return useQuery({
    queryKey,
    queryFn: () => api.get<LiveListResponse>('/admin/live'),
    // SSE is the primary update channel; this long refetch is a
    // safety net if the connection silently drops.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function usePromoteLive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, slot }: { id: string; slot: 1 | 2 | 3 }) =>
      api.post<{ id: string; status: LiveStreamStatus; onAirSlot: number | null }>(
        `/admin/live/${id}/promote`,
        { slot },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });
}

export function useRejectLive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/admin/live/${id}/reject`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });
}

export function useCutLive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => api.post(`/admin/live/${id}/cut`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });
}

// ── OBS / desktop encoder broadcast ───────────────────────────────────────

/**
 * Shape returned by `POST /live/sessions`. The same payload the mobile
 * correspondent flow consumes — for OBS we ignore `pushUrl` (SRT) and
 * show `pushUrlRtmp` instead. `pushUrlRtmp` already includes the stream
 * key as the path segment; the OBS page also splits the URL into the
 * (server, key) pair OBS's settings UI expects.
 */
export interface CreateLiveSessionResponse {
  id: string;
  streamKey: string;
  pushUrl: string;
  pushUrlRtmp: string;
  status: LiveStreamStatus;
}

export function useStartObsSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title?: string; locationLabel?: string }) =>
      api.post<CreateLiveSessionResponse>('/live/sessions', body),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });
}

export function useEndLiveSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => api.delete(`/live/sessions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });
}

// ── Persistent stream keys (OBS / simulcast) ──────────────────────────────

export interface StreamKeyDestination {
  id: string;
  streamKeyId: string;
  platform: string;
  label: string;
  rtmpUrl: string;
  keyMasked: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface StreamKey {
  id: string;
  name: string;
  localeCode: string | null;
  enabled: boolean;
  notes: string | null;
  streamKey: string;
  pushUrlRtmp: string | null;
  pushUrlSrt: string | null;
  createdAt: string;
  updatedAt: string;
  revokedAt: string | null;
  createdBy: { id: string; email: string; name: string | null } | null;
  destinations: StreamKeyDestination[];
}

export interface StreamKeyListResponse {
  items: StreamKey[];
  ingestHost: string | null;
}

const streamKeysKey = ['live', 'keys'] as const;

export function useStreamKeys() {
  return useQuery({
    queryKey: streamKeysKey,
    queryFn: () => api.get<StreamKeyListResponse>('/admin/live/keys'),
    staleTime: 5_000,
  });
}

export function useStreamKey(id: string | null) {
  return useQuery({
    queryKey: ['live', 'keys', id],
    queryFn: () => api.get<StreamKey>(`/admin/live/keys/${id}`),
    enabled: !!id,
  });
}

export function useCreateStreamKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; localeCode?: string | null; notes?: string | null }) =>
      api.post<StreamKey>('/admin/live/keys', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: streamKeysKey }),
  });
}

export function useUpdateStreamKey(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name?: string;
      localeCode?: string | null;
      notes?: string | null;
      enabled?: boolean;
    }) => api.patch<StreamKey>(`/admin/live/keys/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: streamKeysKey });
      qc.invalidateQueries({ queryKey: ['live', 'keys', id] });
    },
  });
}

export function useRotateStreamKey(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<StreamKey>(`/admin/live/keys/${id}/rotate`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: streamKeysKey });
      qc.invalidateQueries({ queryKey: ['live', 'keys', id] });
    },
  });
}

export function useRevokeStreamKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/live/keys/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: streamKeysKey }),
  });
}

export function useCreateStreamKeyDestination(streamKeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      platform: string;
      label: string;
      rtmpUrl: string;
      destinationKey: string;
      enabled?: boolean;
    }) => api.post<StreamKeyDestination>(`/admin/live/keys/${streamKeyId}/destinations`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: streamKeysKey });
      qc.invalidateQueries({ queryKey: ['live', 'keys', streamKeyId] });
    },
  });
}

export function useUpdateStreamKeyDestination(streamKeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      platform?: string;
      label?: string;
      rtmpUrl?: string;
      destinationKey?: string;
      enabled?: boolean;
    }) =>
      api.patch<StreamKeyDestination>(
        `/admin/live/keys/${streamKeyId}/destinations/${id}`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: streamKeysKey });
      qc.invalidateQueries({ queryKey: ['live', 'keys', streamKeyId] });
    },
  });
}

export function useDeleteStreamKeyDestination(streamKeyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete(`/admin/live/keys/${streamKeyId}/destinations/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: streamKeysKey });
      qc.invalidateQueries({ queryKey: ['live', 'keys', streamKeyId] });
    },
  });
}

// ── Archive ────────────────────────────────────────────────────────────────

export interface RecordingListItem {
  id: string;
  streamId: string | null;
  r2Key: string | null;
  sizeBytes: number | null;
  durationSeconds: number | null;
  createdAt: string;
  uploadedAt: string | null;
  localFileDeleted: boolean;
  correspondent: {
    id: string;
    email: string;
    name: string | null;
  } | null;
  title: string | null;
}

export interface RecordingListResponse {
  items: RecordingListItem[];
  nextCursor: string | null;
}

/**
 * Recordings archive list. Cursor pagination (newest first); pass
 * `beforeCreatedAt` to load the next page. Each page is its own
 * react-query key so back-button navigation feels instant.
 */
export function useRecordings(params: { beforeCreatedAt?: string; limit?: number }) {
  return useQuery({
    queryKey: ['live', 'recordings', params.beforeCreatedAt ?? 'first', params.limit ?? 50],
    queryFn: () => api.get<RecordingListResponse>('/admin/live/recordings', params),
  });
}

export interface PresignedRecordingUrl {
  url: string;
  expiresInSeconds: number;
}

/**
 * Fetch a fresh presigned R2 download URL. The URL is short-lived
 * (~10 min) so we mint on demand rather than cache.
 */
export function useFetchRecordingUrl() {
  return useMutation({
    mutationFn: (id: string) =>
      api.get<PresignedRecordingUrl>(`/admin/live/recordings/${id}/url`),
  });
}

// ── Storage usage / retention status ─────────────────────────────────────

export interface RecordingsUsage {
  local: {
    bytes: number;
    count: number;
    maxBytes: number;
    maxCount: number;
    pendingUpload: number;
  };
  r2: {
    bytes: number;
    count: number;
    maxBytes: number;
    configured: boolean;
  };
}

/**
 * Local + R2 storage snapshot. Polled every 30 s on the Recordings
 * archive page so operators see retention pressure in near-real-time
 * (a tick of the cron is 2 min — 30 s gives the UI a refresh between
 * cron sweeps).
 */
export function useRecordingsUsage() {
  return useQuery({
    queryKey: ['live', 'recordings', 'usage'],
    queryFn: () => api.get<RecordingsUsage>('/admin/live/recordings/usage'),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

/**
 * Permanently delete a recording (R2 object + DB row). Invalidates
 * both the paginated list queries and the usage snapshot so the row
 * disappears and the storage bars update immediately.
 */
export function useDeleteRecording() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/live/recordings/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['live', 'recordings'] });
    },
  });
}
